/**
 * Displays user profile information and allows users to manage
 * language preferences and theme settings across desktop and
 * mobile experiences.
 */
import { ChevronLeft, XClose } from "@untitledui/icons";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { useDeviceType } from "@/hooks/useDeviceType";
import { saveUserLanguage } from "@/services/language";
import type { AppDispatch } from "@/store";
import { selectTheme, setThemeAndPersist } from "@/store/theme";
import { setLanguage } from "@/store/userSlice";

interface ProfileModalProps {
  open: boolean;
  onClose: () => void;
  profile: {
    profileDisplayName: string;
    profileEmail: string;
    primaryOrganization?: string;
    organization?: string;
    role?: string;
  } | null;
}


const LANG_OPTIONS = [
  { code: "en", label: "English" },
  { code: "fr", label: "Français" },
];

const CODE_TO_LABEL: Record<string, string> = {
  en: "English",
  fr: "Français",
};

const ProfileModal: React.FC<ProfileModalProps> = ({ open, onClose, profile }) => {
  const { t, i18n } = useTranslation();
  const { isMobile } = useDeviceType();
  const dispatch = useDispatch<AppDispatch>();
  const theme = useSelector(selectTheme);
  const [saving, setSaving] = React.useState(false);

  const currentLang = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];

  const changeLanguage = async (langCode: string) => {
    if (saving || langCode === currentLang) return;

    try {
      setSaving(true);

      i18n.changeLanguage(langCode);

      const languageLabel = CODE_TO_LABEL[langCode] || "English";

      // update Redux immediately
      dispatch(setLanguage(languageLabel));

      await saveUserLanguage({
        upn: profile?.profileEmail || "",
        language: languageLabel,
      });
    } catch (err) {
      console.error("Failed to save language", err);

      // rollback redux + i18n
      dispatch(setLanguage(CODE_TO_LABEL[currentLang]));
      i18n.changeLanguage(currentLang);
    } finally {
      setTimeout(() => setSaving(false), 800);
    }
  };

  if (!open || !profile) return null;

  if (isMobile) {
    return (
      <div
        className="fixed inset-0 z-[1200] bg-[var(--bg-main)]"
        role="dialog"
        aria-modal="true"
        aria-label={t("viewProfile.header")}
      >
        <div className="flex items-center gap-3 px-4 py-2 bg-white border-b">
          <button onClick={onClose} aria-label={t("viewProfile.close")}>
            <ChevronLeft className="w-5 h-5" />
          </button>

          <div>
            <h1 className="text-lg font-semibold">{t("viewProfile.header")}</h1>
            <p className="text-xs text-gray-500">{t("viewProfile.subtitle")}</p>
          </div>
        </div>

        <div className="p-4 space-y-6 overflow-y-auto h-[calc(100vh-56px)]">
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <div className="space-y-3">
              <Input label={t("viewProfile.name")} value={profile.profileDisplayName} />
              <Input label={t("viewProfile.email")} value={profile.profileEmail} />
             <Input
label={t("viewProfile.primaryOrganization")}
  value={profile.primaryOrganization}
/>

<Input
  label={t("viewProfile.organization")}
  value={profile.organization}
/>
              <Input label={t("viewProfile.role")} value={profile.role} />
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold mb-3">{t("components.navBar.language")}</h3>

            <div className="grid grid-cols-2 gap-3">
              {LANG_OPTIONS.map((lang, index) => {
                const active = currentLang === lang.code;

                return (
                  <button
                    key={`${lang.code}-${index}`}
                    type="button"
                    onClick={() => changeLanguage(lang.code)}
                    disabled={saving}
                    aria-label={t("viewProfile.languageOption", { lang: lang.label })}
                    className={`p-3 rounded-xl border text-left ${
                      active ? "border-orange-500 text-orange-600" : "bg-white"
                    } ${saving ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                  >
                    <div className="flex justify-between items-center">
                      <span>{lang.label}</span>
                      {active && <span className="text-orange-500 font-semibold text-sm">✓</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold mb-3">{t("themes.title")}</h3>

            <div className="flex gap-3 flex-wrap">
              <ThemeCard
                label={t("themes.light")}
                active={theme === "theme2"}
                onClick={() => dispatch(setThemeAndPersist("theme2"))}
              />
              <ThemeCard
                label={t("themes.vibrant")}
                active={theme === "theme1"}
                onClick={() => dispatch(setThemeAndPersist("theme1"))}
              />
              <ThemeCard
                label={t("themes.dark")}
                active={theme === "theme3"}
                onClick={() => dispatch(setThemeAndPersist("theme3"))}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[1200] flex justify-end bg-black/30"
      role="dialog"
      aria-modal="true"
      aria-labelledby="profile-modal-title"
    >
      <div className="w-[420px] max-w-[95vw] h-full shadow-xl border-l border-[var(--border-color)] bg-[var(--panel-bg)]">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <div id="profile-modal-title" className="text-md font-semibold">
              {t("viewProfile.header")}
            </div>
            <div className="text-xs">{t("viewProfile.subtitle")}</div>
          </div>

          <button onClick={onClose} aria-label={t("viewProfile.close")}>
            <XClose className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-6 overflow-y-auto h-full">
         <Input label={t("viewProfile.name")} value={profile.profileDisplayName} />
<Input label={t("viewProfile.email")} value={profile.profileEmail} />

<Input
  label={t("viewProfile.primaryOrganization")}
  value={profile.primaryOrganization}
/>

<Input
  label={t("viewProfile.organization")}
  value={profile.organization}
/>

<Input label={t("viewProfile.role")} value={profile.role} />
        </div>
      </div>
    </div>
  );
};

const Input = ({ label, value }: { label: string; value?: string }) => (
  <div>
    <label className="block text-xs mb-1">{label}</label>
    <input readOnly value={value || ""} className="w-full px-3 py-2 rounded-md border bg-gray-50" />
  </div>
);

const ThemeCard = ({
  label,
  active,
  onClick,
}: {
  label: string;
  active?: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    className={`px-4 py-3 rounded-xl border ${
      active ? "border-orange-500 text-orange-600" : "bg-white"
    }`}
  >
    {label}
  </button>
);

export default ProfileModal;
